import { useCallback } from "react";
import { UseMutationOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  PqlActionedInput,
  PqlActionedMutation,
  PqlActionedMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { EventData, EventNames, useTracking } from "lib/tracking";
import { PqlActionType } from "lib/types/graphQLEnums";
import { formatDataType } from "../../../utils";
import { useGraphQlMutation } from "../useGraphQlClient";

const pqlActionedMutation = graphql(`
  mutation pqlActioned($input: PqlActionedInput!) {
    pqlActioned(input: $input) {
      success
    }
  }
`);

// Amplitude's paywallActioned events record the label, not the GraphQL enum name.
const PQL_ACTION_LABELS: Record<PqlActionType, string> = {
  [PqlActionType.BookADemo]: "Book a demo",
  [PqlActionType.SpeakToSales]: "Speak to sales",
  [PqlActionType.CompareAllPlans]: "Compare all plans",
  [PqlActionType.ViewPricing]: "View pricing",
  [PqlActionType.Upgrade]: "Upgrade",
  [PqlActionType.UpgradeToBasic]: "Upgrade to basic",
  [PqlActionType.BookADemoInApp]: "Book a demo in-app",
  [PqlActionType.BookADemoInAppFormSubmission]: "Book a demo in-app form submission",
  [PqlActionType.SpeakToYourCsm]: "Speak to your CSM",
};

export function usePqlActioned(
  options?: UseMutationOptions<PqlActionedMutation, unknown, PqlActionedMutationVariables, unknown>,
  trackingData?: EventData & { "Connected provider"?: string },
  trackEvent = true,
) {
  const { logEvent } = useTracking();

  const mutation = useGraphQlMutation(pqlActionedMutation, {
    ...options,
    onSuccess: (data, variables, context) => {
      const { input } = variables;
      if (trackEvent) {
        logEvent(EventNames.paywallActioned, {
          "Data type": input.dataType ?? "N/A",
          Action: PQL_ACTION_LABELS[input.actionType],
          ...trackingData,
          "Connected provider": trackingData?.["Connected provider"] ?? "N/A",
        });
      }
      options?.onSuccess?.(data, variables, context);
    },
  });

  const { mutate: mutateInput } = mutation;
  // Callers send the request alone; the data type falls back to the one in the page URL.
  const mutate = useCallback(
    (request: PqlActionedInput) =>
      mutateInput({
        input: { ...request, dataType: request.dataType ?? formatDataType(window.location.href) },
      }),
    [mutateInput],
  );

  return { ...mutation, mutate };
}
